import React, { useState, useEffect } from 'react';
import { Bookmark, BookmarkCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import errMsg from '../utils/apiError';

/**
 * BookmarkButton — Toggle a bookmark on any content item.
 * Props:
 *  - itemType: 'message' | 'victory' | 'question' | 'resource' | 'event' | 'story' | 'reward'
 *  - itemId:    string
 *  - itemTitle: string  (short label shown in Mon Carnet)
 *  - itemPreview?: string
 *  - itemUrl?: string
 *  - size?: 'sm' | 'md'
 *  - variant?: 'ghost' | 'outline'
 */
export default function BookmarkButton({
  itemType,
  itemId,
  itemTitle,
  itemPreview = '',
  itemUrl = '',
  size = 'sm',
  variant = 'ghost',
  className = '',
}) {
  const { api } = useAuth();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!itemType || !itemId) return;
    let cancelled = false;
    api.get(`/bookmarks/check?item_type=${itemType}&item_id=${itemId}`)
      .then((r) => { if (!cancelled) setIsBookmarked(r.data.is_bookmarked); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, itemType, itemId]);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (loading) return;
    setLoading(true);
    try {
      if (isBookmarked) {
        await api.delete(`/bookmarks/by-item?item_type=${itemType}&item_id=${itemId}`);
        setIsBookmarked(false);
        toast.success('Retiré de ton Carnet');
      } else {
        await api.post('/bookmarks', {
          item_type: itemType,
          item_id: itemId,
          item_title: itemTitle || '(sans titre)',
          item_preview: itemPreview,
          item_url: itemUrl,
        });
        setIsBookmarked(true);
        toast.success('Ajouté à ton Carnet 📓');
      }
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setLoading(false);
    }
  };

  const Icon = isBookmarked ? BookmarkCheck : Bookmark;
  const iconSizeClass = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
  const btnSizeClass = size === 'sm' ? 'h-6 w-6' : 'h-8 w-8';

  return (
    <Button
      variant={variant}
      size="icon"
      onClick={toggle}
      disabled={loading}
      className={`${btnSizeClass} ${isBookmarked ? 'text-primary' : ''} ${className}`}
      title={isBookmarked ? 'Retirer du Carnet' : 'Ajouter au Carnet'}
      data-testid={`bookmark-toggle-${itemType}-${itemId}`}
    >
      <Icon className={iconSizeClass} />
    </Button>
  );
}
